/* Presentation only. Every purchase goes through the existing Dawn button. */
(() => {
  if (customElements.get('craft-product-sticky')) return;
  const instances = new Map();
  const overlaySelector = 'cart-drawer.active, .cart-notification.active, quick-add-modal[open], modal-dialog[open], product-modal[open], header-drawer > details[open], details-modal > details[open]';

  class CraftProductSticky extends HTMLElement {
    connectedCallback() {
      if (this.moving || this.initialized) return;
      const source = this.closest('product-info');
      if (!source || source.dataset.updateUrl === 'false' || this.closest('quick-add-modal, product-modal')) return;
      this.sectionId = this.dataset.sectionId;
      if (!this.sectionId || this.sectionId.startsWith('quickadd-')) return;

      instances.get(this.sectionId)?.remove();
      instances.set(this.sectionId, this);
      this.initialized = true;
      this.source = source;
      this.anchor = document.createComment(`craft-sticky:${this.sectionId}`);
      this.before(this.anchor);
      this.moving = true;
      document.body.appendChild(this);
      this.moving = false;

      this.submitElement = this.querySelector('[data-cp-sticky-submit]');
      this.labelElement = this.querySelector('[data-cp-sticky-label]');
      this.titleElement = this.querySelector('[data-cp-sticky-title]');
      this.priceElement = this.querySelector('[data-cp-sticky-price]');
      this.statusElement = this.querySelector('[data-cp-sticky-status]');
      this.mobile = window.matchMedia('(max-width: 749px)');
      this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.onViewport = () => this.queue(false);
      this.onState = () => this.queue(true);
      this.onSubmit = event => this.delegate(event);
      this.onUnload = event => {
        if (event.target.contains(this.anchor)) this.remove();
      };

      this.submitElement.addEventListener('click', this.onSubmit);
      window.addEventListener('scroll', this.onViewport, { passive: true });
      window.addEventListener('resize', this.onViewport, { passive: true });
      window.visualViewport?.addEventListener('resize', this.onViewport);
      this.mobile.addEventListener('change', this.onViewport);
      document.addEventListener('focusin', this.onViewport);
      document.addEventListener('focusout', this.onViewport);
      document.addEventListener('shopify:section:load', this.onState);
      document.addEventListener('shopify:section:unload', this.onUnload);
      document.addEventListener('product-info:loaded', this.onState);

      this.sourceObserver = new MutationObserver(this.onState);
      this.documentObserver = new MutationObserver(records => {
        if (!this.anchor.isConnected) return this.remove();
        const changed = records.some(record => {
          if (record.target === this || this.contains(record.target)) return false;
          if (record.target === document.body) return true;
          if (!this.nativeButton?.isConnected || !this.footer?.isConnected) return true;
          return record.target instanceof Element && record.target.matches('cart-drawer, .cart-notification, quick-add-modal, modal-dialog, product-modal, header-drawer > details, details-modal > details');
        });
        if (changed) this.queue(true);
      });
      this.documentObserver.observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['class', 'open', 'hidden'] });
      if ('IntersectionObserver' in window) this.intersections = new IntersectionObserver(this.onViewport, { threshold: 0 });
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.measure());
        this.resizeObserver.observe(this);
      }
      this.queue(true);
    }

    disconnectedCallback() {
      if (this.moving || !this.initialized) return;
      this.initialized = false;
      cancelAnimationFrame(this.frame);
      this.sourceObserver?.disconnect();
      this.documentObserver?.disconnect();
      this.intersections?.disconnect();
      this.resizeObserver?.disconnect();
      this.source?.removeEventListener('change', this.onState);
      this.submitElement?.removeEventListener('click', this.onSubmit);
      window.removeEventListener('scroll', this.onViewport);
      window.removeEventListener('resize', this.onViewport);
      window.visualViewport?.removeEventListener('resize', this.onViewport);
      this.mobile?.removeEventListener('change', this.onViewport);
      document.removeEventListener('focusin', this.onViewport);
      document.removeEventListener('focusout', this.onViewport);
      document.removeEventListener('shopify:section:load', this.onState);
      document.removeEventListener('shopify:section:unload', this.onUnload);
      document.removeEventListener('product-info:loaded', this.onState);
      this.anchor?.remove();
      if (instances.get(this.sectionId) === this) instances.delete(this.sectionId);
      if (![...instances.values()].some(instance => !instance.hidden)) {
        document.body.classList.remove('cp-sticky-visible');
        document.documentElement.style.removeProperty('--cp-sticky-height');
      }
    }

    queue(sync) {
      if (!this.initialized) return;
      this.needsSync = this.needsSync || sync;
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = 0;
        if (!this.anchor.isConnected) return this.remove();
        if (this.needsSync || !this.nativeButton?.isConnected) {
          this.needsSync = false;
          this.connectControls();
          this.mirror();
        }
        this.updateVisibility();
      });
    }

    connectControls() {
      const form = document.getElementById(this.dataset.nativeFormId);
      const source = form?.closest('product-info');
      const button = form?.querySelector('.product-form__submit');
      const footer = document.querySelector('footer.footer, .footer');
      if (source && source !== this.observedSource) {
        this.observedSource?.removeEventListener('change', this.onState);
        this.sourceObserver.disconnect();
        this.source = source;
        this.observedSource = source;
        this.sourceObserver.observe(source, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['disabled', 'aria-disabled', 'aria-busy', 'aria-haspopup', 'class', 'hidden', 'value', 'id', 'style'] });
        source.addEventListener('change', this.onState);
      }
      if (button !== this.nativeButton || footer !== this.footer) {
        this.nativeButton = button;
        this.footer = footer;
        this.intersections?.disconnect();
        if (button) this.intersections?.observe(button);
        if (footer) this.intersections?.observe(footer);
      }
      this.nativeForm = form;
      this.productForm = form?.closest('product-form');
      this.variantInput = form?.querySelector('[name="id"]');
    }

    mirror() {
      if (!this.nativeButton) {
        this.submitElement.disabled = true;
        return;
      }
      const native = this.nativeButton;
      const label = [...native.children].find(child => child.tagName === 'SPAN' && !child.hidden && !child.classList.contains('hidden') && !child.classList.contains('visually-hidden'))?.textContent.trim() || native.getAttribute('aria-label') || native.textContent.trim();
      const busy = native.classList.contains('loading') || native.getAttribute('aria-busy') === 'true';
      const disabled = native.disabled || native.getAttribute('aria-disabled') === 'true' || busy || !this.variantInput?.value || this.variantInput.disabled;
      const title = this.source.querySelector('.product__title h1')?.textContent.trim() || this.titleElement.textContent.trim();
      this.submitElement.disabled = disabled;
      this.submitElement.setAttribute('aria-disabled', String(disabled));
      this.submitElement.setAttribute('aria-busy', String(busy));
      this.submitElement.classList.toggle('loading', busy);
      this.classList.toggle('is-loading', busy);
      this.statusElement.hidden = !busy;
      if (this.labelElement.textContent !== label) this.labelElement.textContent = label;
      if (this.titleElement.textContent !== title) this.titleElement.textContent = title;
      this.submitElement.setAttribute('aria-label', `${label}: ${title}`);
      if (native.hasAttribute('aria-haspopup')) this.submitElement.setAttribute('aria-haspopup', native.getAttribute('aria-haspopup'));
      else this.submitElement.removeAttribute('aria-haspopup');
      this.dataset.variantId = this.variantInput?.value || '';

      const price = document.getElementById(`price-${this.sectionId}`);
      const priceVisible = price && !price.hidden && !price.classList.contains('hidden') && getComputedStyle(price).display !== 'none';
      const html = priceVisible ? price.innerHTML : '';
      if (html !== this.priceHTML) {
        this.priceHTML = html;
        const template = document.createElement('template');
        template.innerHTML = html;
        template.content.querySelectorAll('[id]').forEach(element => element.removeAttribute('id'));
        this.priceElement.replaceChildren(template.content.cloneNode(true));
      }
      this.priceElement.hidden = !priceVisible;

      const error = this.productForm?.querySelector('.product-form__error-message-wrapper');
      const message = error && !error.hidden ? error.textContent.trim() : '';
      if (this.pendingStickySubmit && message && message !== this.lastError) {
        this.pendingStickySubmit = false;
        this.revealError(error);
      }
      this.lastError = message;
      if (this.wasBusy && !busy) this.pendingStickySubmit = false;
      this.wasBusy = busy;
    }

    updateVisibility() {
      const dialog = Boolean(document.querySelector(overlaySelector));
      const editing = document.activeElement?.matches('input, textarea, select, [contenteditable="true"]');
      const rect = this.nativeButton?.getBoundingClientRect();
      const viewportHeight = window.visualViewport?.height || window.innerHeight;
      const footer = this.footer?.getBoundingClientRect();
      const footerVisible = footer && footer.top < viewportHeight && footer.bottom > 0;
      const show = Boolean(this.mobile.matches && this.nativeButton?.isConnected && this.nativeButton.getClientRects().length && rect.bottom <= 0 && !footerVisible && !dialog && !editing);
      this.hidden = !show;
      this.style.display = show ? '' : 'none';
      this.classList.toggle('is-visible', show);
      document.body.classList.toggle('cp-sticky-visible', show);
      if (show) this.measure();
      else document.documentElement.style.removeProperty('--cp-sticky-height');

      const drawer = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
      if (this.dialogWasOpen && !dialog && drawer?.activeElement === this.submitElement) {
        const active = document.activeElement;
        if (active === document.body || active?.closest('cart-drawer, cart-notification')) {
          const target = show ? this.submitElement : document.getElementById('cart-icon-bubble');
          target?.focus({ preventScroll: true });
        }
      }
      this.dialogWasOpen = dialog;
    }

    measure() {
      if (this.hidden) return;
      document.documentElement.style.setProperty('--cp-sticky-height', `${Math.ceil(this.getBoundingClientRect().height)}px`);
    }

    delegate(event) {
      event.preventDefault();
      this.connectControls();
      this.mirror();
      if (!this.nativeButton || this.submitElement.disabled) return;
      this.pendingStickySubmit = true;
      this.lastError = '';
      this.nativeButton.click();
      this.wasBusy = this.nativeButton.classList.contains('loading') || this.nativeButton.getAttribute('aria-busy') === 'true';
      this.queue(true);
    }

    revealError(error) {
      const rect = error.getBoundingClientRect();
      if (rect.top < 0 || rect.bottom > window.innerHeight) {
        error.scrollIntoView({ block: 'center', behavior: this.reduced.matches ? 'auto' : 'smooth' });
      }
      if (!error.hasAttribute('tabindex')) error.setAttribute('tabindex', '-1');
      error.focus({ preventScroll: true });
    }
  }

  customElements.define('craft-product-sticky', CraftProductSticky);
})();
